import {
  useState,
  useRef,
  MouseEvent as ReactMouseEvent,
  RefObject,
} from "react";
import classNames from "classnames/bind";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import { GalleryImageObject } from "../utils/utils";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import styles from "./ZoomModalImage.module.scss";

const cx = classNames.bind(styles);

interface ZoomModalImageProps {
  mediaItem: GalleryImageObject;
  index: number;
  total: number;
  handleSuperZoomOpen: (
    event: ReactMouseEvent<HTMLButtonElement>,
    index: number,
    imageRef: RefObject<HTMLButtonElement | null>,
  ) => void;
}

export const ZoomModalImage = ({
  mediaItem,
  index,
  total,
  handleSuperZoomOpen,
}: ZoomModalImageProps) => {
  const imageRef = useRef<HTMLButtonElement>(null);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const { flags } = useStandardProps();
  const premiumSuperZoomEnabled = flags.premiumSuperZoom.value === true;

  const srcSetValues = [
    "$rsp-pdp-port-320$ 320w",
    "$rsp-pdp-port-640$ 640w",
    "$rsp-pdp-port-1080$ 1080w",
    "$rsp-pdp-port-1440$ 1440w",
  ];
  const generateSrcSetUrl = (rspValue: string) =>
    formatAmplienceImageURL(`${mediaItem.imageUrl}?${rspValue}`);

  const handleOnClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    handleSuperZoomOpen(event, index, imageRef);
  };

  return (
    <button
      ref={imageRef}
      className={styles.imageButton}
      type="button"
      aria-label={`Product image ${index + 1} of ${total}, ${mediaItem.title}`}
      data-testid={`gallery:zoom:modal:image-${index}`}
      onClick={handleOnClick}
    >
      <img
        src={formatAmplienceImageURL(mediaItem.imageUrl + "?wid=640&hei=853")}
        loading="eager"
        className={cx(styles.initialImage, {
          hide: mainImageLoaded,
        })}
        alt={`Product image ${index + 1} of ${total}, ${mediaItem.title}`}
      />
      <img
        src={formatAmplienceImageURL(mediaItem.imageUrl)}
        srcSet={`${srcSetValues.map((v) => generateSrcSetUrl(v)).join(",")}`}
        sizes="100vw"
        loading="eager"
        className={cx(styles.mainImage, {
          show: mainImageLoaded,
          superZoom: premiumSuperZoomEnabled,
        })}
        alt={`Product image ${index + 1} of ${total}, ${mediaItem.title}`}
        onLoad={() => {
          setMainImageLoaded(true);
        }}
      />
    </button>
  );
};
